// Pure filter helpers for the tech app's active job list (TechDashboard.jsx).
//
// Root cause (2026-09-22, tech2 report): techs were seeing jobs from over a week
// ago that were already paid/invoiced/cancelled in what should be their current
// job list. Two separate gaps:
//
//   1. TechDashboard fetched a full 1-month-back/2-months-forward window, and the
//      status-only tabs (Scheduled/En Route/In Progress/Completed) applied no date
//      restriction at all on top of that -- so a tap on e.g. "Completed" showed
//      every completed job in that ~3 month window, not just recent/current ones.
//   2. The "done, nothing left for the tech to do" check only looked at
//      custom_job_status (the tech's own Scheduled/En Route/In Progress/
//      Completed/Cancelled toggle). That field goes stale when the OFFICE closes a
//      job out -- payment collected, invoiced, cancelled, or declined -- without the
//      tech ever touching their own status buttons on that record again. The
//      server-computed custom_pipeline_status (see
//      erpnext_custom_app/integrity_flow_custom/events/quotation.py
//      _compute_pipeline_status) is the authoritative signal for that and was never
//      consulted here.
//
// Fix: scope the fetch itself to the current week (see getWeekRange, used by both
// TechDashboard.jsx and TechSchedule.jsx) and fold custom_pipeline_status into the
// inactive check.

// custom_pipeline_status values (Quotation) that mean the job is closed out from
// the office's side -- paid, invoiced, cancelled, or declined -- regardless of
// what custom_job_status still says. Keep in sync with the LOCKED 14-label set in
// admin-frontend/src/services/crmBackendService.js's PIPELINE_STATUS_COLORS.
// Deliberately excludes 'Approved'/'Confirmed'/'Sent for Approval'/
// 'Reschedule Needed' -- those still need tech action.
export const CLOSED_PIPELINE_STATUSES = new Set([
  'Paid',
  'Invoiced',
  'Past Due',
  'Closed',
  'Canceled',
  'Declined',
]);

// Same fallback as crmBackendService.js's normalizeJobStatus: blank/null/undefined
// custom_job_status (Quotations created before the field existed) reads as 'Scheduled'.
export function normalizeJobStatus(value) {
  return value || 'Scheduled';
}

// True when there's nothing left for a tech to do on this job: it's Completed/
// Cancelled per the tech's own status, OR the office has already closed it out
// (paid, invoiced, cancelled, declined) per the authoritative pipeline status.
export function isJobInactiveForTechList(job) {
  const jobStatus = normalizeJobStatus(job?.custom_job_status);
  if (jobStatus === 'Completed' || jobStatus === 'Cancelled') return true;
  return Boolean(job?.custom_pipeline_status && CLOSED_PIPELINE_STATUSES.has(job.custom_pipeline_status));
}

// Sunday-Saturday week containing `now` (default: today). Shared convention
// between TechDashboard.jsx and TechSchedule.jsx.
export function getWeekRange(now = new Date()) {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay());
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return {
    startStr: start.toISOString().split('T')[0],
    endStr: end.toISOString().split('T')[0],
  };
}

// In-week (inclusive) AND not already closed out.
export function isThisWeekActiveJob(job, startStr, endStr) {
  const date = job?.custom_scheduled_date;
  if (!date || date < startStr || date > endStr) return false;
  return !isJobInactiveForTechList(job);
}
